<template>
  <div class="medicaiton-prescription-overview">
    <div class="dss__subheader">
      <div class="dss__subheader-main mb-6 mt-5">
        <smart-icon icon="mdi-heart-outline" size="16"/>
        <h3 class="ml-2">{{ $ucfirst($t("myWeekMedication.cardioMedication", { catalog: 'app_mobile' })) }}</h3>
      </div>
    </div>
    <medication
      v-for="(medication, index) in cardioMedicationList"
      :key="`schedule-${medication.medicationSchedule.id}`"
      :index="index"
      :value="medication.medicationSchedule"
      :single-class="false"
      :has-history="false"
      :has-recommendation-info="false"
      :medicationSchedulesWithRecommendationInfo="medication"
      is-read-only
    ></medication>
    <div
      v-if="!cardioMedicationList.length"
      class="medication-list__empty mb-6"
    >{{ $ucfirst($t('patient.medicationListEmpty')) }}</div>
    <div class="dss__subheader">
      <div class="dss__subheader-main mb-6 mt-8">
        <smart-icon icon="mdi-heart-off-outline" size="16"/>
        <h3 class="ml-2">{{ $ucfirst($t("myWeekMedication.otherMedication", { catalog: 'app_mobile' })) }}</h3>
      </div>
    </div>
    <medication
      v-for="(medication, index) in otherMedicationList"
      :key="`schedule-${medication.medicationSchedule.id}`"
      :index="index"
      :value="medication.medicationSchedule"
      :has-medication-class="false"
      :has-notes="false"
      :has-history="false"
      :has-recommendation-info="false"
      is-read-only
      :medicationSchedulesWithRecommendationInfo="medication"
    ></medication>
    <div
      v-if="!otherMedicationList.length"
      class="medication-list__empty mb-6"
    >{{ $ucfirst($t('patient.medicationListEmpty')) }}</div>
    <div
      v-if="hasDSSTitrationAccess"
      class="dss__subheader"
    >
      <div class="dss__subheader-main mb-6 mt-8">
        <div>
          <svg style="width:16px;height:16px" viewBox="0 0 24 24">
            <path fill="currentColor" d="M6,22A3,3 0 0,1 3,19C3,18.4 3.18,17.84 3.5,17.37L9,7.81V6A1,1 0 0,1 8,5V4A2,2 0 0,1 10,2H14A2,2 0 0,1 16,4V5A1,1 0 0,1 15,6V7.81L20.5,17.37C20.82,17.84 21,18.4 21,19A3,3 0 0,1 18,22H6M5,19A1,1 0 0,0 6,20H18A1,1 0 0,0 19,19C19,18.79 18.93,18.59 18.82,18.43L16.53,14.47L14,17L8.93,11.93L5.18,18.43C5.07,18.59 5,18.79 5,19M13,10A1,1 0 0,0 12,11A1,1 0 0,0 13,12A1,1 0 0,0 14,11A1,1 0 0,0 13,10Z" />
          </svg>
        </div>
        <h3 class="ml-2 pb-1">{{ $ucfirst($t("dss.titrationSchemes")) }}</h3>
      </div>
    </div>
    <v-row v-if="hasDSSTitrationAccess">
      <v-col v-if="titrationSchemes.length" class="py-0">
        <span v-for="scheme in titrationSchemes" v-bind:key="scheme.id">
          <dosage-titration-scheme-card
            :titrationScheme="scheme"
            v-if="scheme.type == 'DOSAGE'"
            readonly
          />
          <drug-addition-titration-scheme-card
            :titrationScheme="scheme"
            v-if="scheme.type == 'DRUG_ADDITION'"
            readonly
          />
        </span>
      </v-col>
      <v-col class="py-0" v-else>
        {{ $ucfirst($t("dss.noTitrationSchemesRegistered")) }}
      </v-col>
    </v-row>
    <div class="d-flex justify-end mb-8 mt-4">
      <v-btn
        x-large
        color="primary"
        text
        class="mr-3"
        :to="{ name: 'PatientOverview', params: { patientId: patientId } }"
      >
        {{ $ucfirst($t("dss.goToPatientOverview")) }}
        <eva-icon
          class="encounter-interface__icon"
          name="arrow-right-outline"
        />
      </v-btn>
    </div>
  </div>
</template>

<script>
import SmartIcon from "./../../components/Parts/SmartIcon.vue";
import DosageTitrationSchemeCard from "../../components/Parts/DSS/DosageTitrationSchemeCard.vue";
import DrugAdditionTitrationSchemeCard from "../../components/Parts/DSS/DrugAdditionTitrationSchemeCard.vue";
import Medication from '../../components/Parts/Medication/Medication';

/**
 * Component DSSMedicationPrescription.vue
 *
 * @module DSSMedicationPrescription
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.10] app/src/APP_DASHBOARD/templates/views/DSS/DSSMedicationPrescription.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

import { mapGetters } from "vuex";
import gql from "graphql-tag";

export default {
  name: "DSSMedicationPrescription",
  components: {
    Medication,
    SmartIcon,
    DosageTitrationSchemeCard,
    DrugAdditionTitrationSchemeCard,
  },
  computed: {
    ...mapGetters({
      cardioMedicationList: 'dss/cardioMedicationSchedulesWithRecommendationInfo',
      otherMedicationList: 'dss/otherMedicationSchedulesWithRecommendationInfo',
      patientId: "patientSummary/id",
      hasDSSTitrationAccess: "user/hasDSSTitrationAccess"
    }),
  },
  data: function () {
    return {
      showDialog: false,
      cardioMedicationSchedules: [],
      otherMedicationSchedules: [],
      titrationSchemes: [],
    };
  },
  mounted: function () {
    this.getTitrationSchemes();
  },
  methods: {
    getTitrationSchemes() {
      this.$app.apolloClient
        .query({
          query: gql`
            query getTitrationSchemes($patientId: String!) {
              patientTitrationSchemes(
                functions: [{ function: patientId, parameters: [$patientId] }]
                orders: [{ field: createdAt, direction: asc }]
              ) {
                id
                type
                name
                description
                startClasses {
                  key
                  name
                }
                additionalClasses {
                  key
                  name
                }
                startDosage {
                  doseAndRate {
                    id
                    sequence
                    doseQuantity {
                      code
                      value
                    }
                  }
                }
                endDosage {
                  doseAndRate {
                    id
                    sequence
                    doseQuantity {
                      code
                      value
                    }
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
          },
        })
        .then((result) => {
          this.titrationSchemes = result.data.patientTitrationSchemes;
        });
    },
  },
};
</script>
